import { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { MaintCardShell } from './MaintCardShell';
import { ConfirmApplyDialog } from './ConfirmApplyDialog';

export default function ContactStatesCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  const scan = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/maintenance/contact-states/scan');
      setData(res.data);
      const all = {};
      (res.data.mismatches || []).forEach((_, i) => { all[i] = true; });
      setSelected(all);
    } catch (e) { toast.error(e.response?.data?.detail || 'Scan failed'); }
    finally { setLoading(false); }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const apply = async () => {
    setApplying(true);
    try {
      const corrections = (data.mismatches || [])
        .filter((_, i) => selected[i])
        .map((m) => ({ collection: m.collection, id: m.id, field: m.field, new_state: m.expected_state }));
      const res = await api.post('/admin/maintenance/contact-states/apply', { corrections });
      toast.success(`${res.data.total_updated} state(s) corrected`);
      setConfirmOpen(false);
      scan();
    } catch (e) { toast.error(e.response?.data?.detail || 'Apply failed'); }
    finally { setApplying(false); }
  };

  return (
    <MaintCardShell
      title="Contact / job address states"
      description="Compares each stored state against the ZIP code's real state (USPS ranges) — e.g. a Galveston, TX 77551 address saved as FL. Covers contacts, estimates, invoices, jobs, permits and projects (job site address)."
      testId="contact-states"
      count={data ? data.total_affected : null}
      loading={loading}
      onScan={scan}
    >
      {data && data.mismatches.length > 0 && (
        <div className="space-y-3">
          <div className="max-h-72 overflow-y-auto border border-gray-100 rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-500">
                <tr><th className="p-2 text-left w-8"></th><th className="p-2 text-left">Record</th><th className="p-2 text-left">Zip</th><th className="p-2 text-left">Current</th><th className="p-2 text-left">Expected</th></tr>
              </thead>
              <tbody>
                {data.mismatches.map((m, i) => (
                  <tr key={i} className="border-t border-gray-50" data-testid={`contact-states-row-${i}`}>
                    <td className="p-2"><Checkbox data-testid={`contact-states-row-${i}-checkbox`} checked={!!selected[i]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [i]: !!v }))} /></td>
                    <td className="p-2 text-gray-700">{m.collection} · {m.label}</td>
                    <td className="p-2 text-gray-500">{m.zip}</td>
                    <td className="p-2 text-red-600">{m.current_state || '(empty)'}</td>
                    <td className="p-2 text-emerald-700 font-semibold">{m.expected_state}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button data-testid="contact-states-apply-btn" size="sm" disabled={selectedCount === 0} onClick={() => setConfirmOpen(true)} className="bg-red-600 hover:bg-red-700 text-white">
            Apply selected ({selectedCount})
          </Button>
        </div>
      )}
      {data && data.mismatches.length === 0 && <p className="text-xs text-emerald-600">No mismatches found.</p>}

      <ConfirmApplyDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={apply}
        confirming={applying}
        title="Correct address states"
        summary={`This will correct the state field on ${selectedCount} selected record(s) to match their ZIP code.`}
      />
    </MaintCardShell>
  );
}
